import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ChartPieSlice, 
  CheckCircle, 
  Warning, 
  XCircle,
  CaretDown,
  PencilSimple,
  FloppyDisk
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeltaTracking() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/delta`),
        axios.get(`${API}/delta/stats`)
      ]);
      setItems(itemsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      clause: item.clause,
      title: item.title,
      description: item.description,
      status: item.status,
      priority: item.priority,
      notes: item.notes
    });
  };

  const handleSave = async (itemId) => {
    try {
      await axios.put(`${API}/delta/${itemId}`, editData);
      toast.success("Élément mis à jour");
      setEditingId(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "conforme": return <CheckCircle weight="fill" className="w-5 h-5 text-cyber-success" />;
      case "partiel": return <Warning weight="fill" className="w-5 h-5 text-cyber-volt" />;
      case "non_conforme": return <XCircle weight="fill" className="w-5 h-5 text-cyber-danger" />;
      default: return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "conforme": return "Conforme";
      case "partiel": return "Partiel";
      case "non_conforme": return "Non conforme";
      default: return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical": return "text-cyber-danger";
      case "high": return "text-orange-400";
      case "medium": return "text-cyber-volt";
      case "low": return "text-slate-400";
      default: return "text-slate-400";
    }
  };

  const filteredItems = filter === "all" 
    ? items 
    : items.filter(item => item.status === filter);

  // Prepare radar chart data by category
  const categories = [...new Set(items.map(item => {
    if (item.clause.startsWith("A.5")) return "Politiques";
    if (item.clause.startsWith("A.6")) return "Personnes";
    if (item.clause.startsWith("A.7")) return "Actifs";
    if (item.clause.startsWith("A.8")) return "Technologie";
    return "Autre";
  }))];

  const radarData = categories.map(cat => {
    const categoryItems = items.filter(item => {
      if (cat === "Politiques") return item.clause.startsWith("A.5");
      if (cat === "Personnes") return item.clause.startsWith("A.6");
      if (cat === "Actifs") return item.clause.startsWith("A.7");
      if (cat === "Technologie") return item.clause.startsWith("A.8");
      return false;
    });
    
    const score = categoryItems.reduce((acc, item) => {
      if (item.status === "conforme") return acc + 100;
      if (item.status === "partiel") return acc + 50;
      return acc;
    }, 0) / (categoryItems.length || 1);
    
    return { category: cat, score: Math.round(score), fullMark: 100 };
  });

  return (
    <div data-testid="delta-tracking-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight mb-2">
            Suivi Delta ISO 27001
          </h1>
          <p className="text-slate-400">Suivez votre progression vers la certification</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-cyber-primary font-mono">Chargement...</div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="cyber-card p-6"
              data-testid="stat-total"
            >
              <p className="text-sm text-slate-400 mb-1">Score global</p>
              <div className="flex items-end gap-2">
                <span className="font-mono text-4xl text-cyber-primary">
                  {stats?.compliance_score || 0}
                </span>
                <span className="text-slate-400 text-lg mb-1">%</span>
              </div>
              <div className="progress-cyber mt-3">
                <div 
                  className="progress-cyber-fill" 
                  style={{ width: `${stats?.compliance_score || 0}%` }}
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="cyber-card p-6"
              data-testid="stat-conforme"
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle weight="fill" className="w-4 h-4 text-cyber-success" />
                <p className="text-sm text-slate-400">Conforme</p>
              </div>
              <p className="font-mono text-3xl text-white">{stats?.conforme || 0}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="cyber-card p-6"
              data-testid="stat-partiel"
            >
              <div className="flex items-center gap-2 mb-1">
                <Warning weight="fill" className="w-4 h-4 text-cyber-volt" />
                <p className="text-sm text-slate-400">Partiel</p>
              </div>
              <p className="font-mono text-3xl text-white">{stats?.partiel || 0}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="cyber-card p-6"
              data-testid="stat-non-conforme"
            >
              <div className="flex items-center gap-2 mb-1">
                <XCircle weight="fill" className="w-4 h-4 text-cyber-danger" />
                <p className="text-sm text-slate-400">Non conforme</p>
              </div>
              <p className="font-mono text-3xl text-white">{stats?.non_conforme || 0}</p>
            </motion.div>
          </div>

          {/* Radar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="cyber-card p-6 mb-8"
          >
            <h3 className="font-heading font-bold text-lg mb-4">Conformité par domaine</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#262D3D" />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ fill: "#94A3B8", fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={30} 
                    domain={[0, 100]}
                    tick={{ fill: "#94A3B8", fontSize: 10 }}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#00E5FF"
                    fill="#00E5FF"
                    fillOpacity={0.3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                filter === "all" 
                  ? "bg-cyber-primary/10 text-cyber-primary border-cyber-primary/30" 
                  : "border-cyber-border text-slate-400 hover:border-cyber-primary/50"
              }`}
              data-testid="filter-all"
            >
              Tout ({items.length})
            </button>
            <button
              onClick={() => setFilter("conforme")}
              className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                filter === "conforme" 
                  ? "bg-cyber-success/10 text-cyber-success border-cyber-success/30" 
                  : "border-cyber-border text-slate-400 hover:border-cyber-success/50"
              }`}
              data-testid="filter-conforme"
            >
              Conforme ({stats?.conforme || 0})
            </button>
            <button
              onClick={() => setFilter("partiel")}
              className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                filter === "partiel" 
                  ? "bg-cyber-volt/10 text-cyber-volt border-cyber-volt/30" 
                  : "border-cyber-border text-slate-400 hover:border-cyber-volt/50"
              }`}
              data-testid="filter-partiel"
            >
              Partiel ({stats?.partiel || 0})
            </button>
            <button
              onClick={() => setFilter("non_conforme")}
              className={`px-3 py-1 text-sm rounded-sm border transition-colors ${
                filter === "non_conforme" 
                  ? "bg-cyber-danger/10 text-cyber-danger border-cyber-danger/30" 
                  : "border-cyber-border text-slate-400 hover:border-cyber-danger/50"
              }`}
              data-testid="filter-non-conforme"
            >
              Non conforme ({stats?.non_conforme || 0})
            </button>
          </div>

          {/* Items List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="cyber-card overflow-hidden"
          >
            <ScrollArea className="h-[500px]">
              <div className="divide-y divide-cyber-border">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="p-4 hover:bg-cyber-surface-hover transition-colors"
                    data-testid={`delta-item-${item.id}`}
                  >
                    {editingId === item.id ? (
                      // Edit mode
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <span className="badge-clause text-cyber-volt border-cyber-volt">
                            {item.clause}
                          </span>
                          <span className="flex-1 font-medium">{item.title}</span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Statut</label>
                            <Select
                              value={editData.status}
                              onValueChange={(value) => setEditData({ ...editData, status: value })}
                            >
                              <SelectTrigger className="cyber-input">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="conforme">Conforme</SelectItem>
                                <SelectItem value="partiel">Partiel</SelectItem>
                                <SelectItem value="non_conforme">Non conforme</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">Priorité</label>
                            <Select
                              value={editData.priority}
                              onValueChange={(value) => setEditData({ ...editData, priority: value })}
                            >
                              <SelectTrigger className="cyber-input">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Basse</SelectItem>
                                <SelectItem value="medium">Moyenne</SelectItem>
                                <SelectItem value="high">Haute</SelectItem>
                                <SelectItem value="critical">Critique</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">Notes</label>
                          <Textarea
                            value={editData.notes}
                            onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                            className="cyber-input min-h-[80px]"
                            placeholder="Ajouter des notes..."
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleSave(item.id)}
                            className="bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
                            data-testid={`save-delta-${item.id}`}
                          >
                            <FloppyDisk weight="duotone" className="w-4 h-4 mr-2" />
                            Enregistrer
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setEditingId(null)}
                            className="text-slate-400 hover:text-white"
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div className="flex items-start gap-4">
                        <div className="shrink-0 mt-1">
                          {getStatusIcon(item.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="badge-clause text-cyber-volt border-cyber-volt">
                              {item.clause}
                            </span>
                            <span className="font-medium">{item.title}</span>
                          </div>
                          <p className="text-sm text-slate-400 mb-2">{item.description}</p>
                          {item.notes && (
                            <p className="text-sm text-slate-500 italic">Notes: {item.notes}</p>
                          )}
                        </div>
                        <div className="shrink-0 flex items-center gap-4">
                          <span className={`text-xs font-mono ${getPriorityColor(item.priority)}`}>
                            {item.priority.toUpperCase()}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(item)}
                            className="text-slate-400 hover:text-cyber-primary"
                            data-testid={`edit-delta-${item.id}`}
                          >
                            <PencilSimple weight="duotone" className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </ScrollArea>
          </motion.div>
        </>
      )}
    </div>
  );
}
